"use client"

import { Image as ImageIcon, Laptop, PiggyBank, Video } from "lucide-react"
import { motion } from "framer-motion"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import PageContent from "@/components/page-content"
import SectionHeader from "@/components/landing/section-header"
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { useEffect, useMemo, useRef, useState } from "react"

import {
  AI_INSPECTION_STOPS,
  BUNDLING_PRESETS,
  DEFAULT_AI_INSPECTION_INDEX,
  FRAME_RATE_OPTIONS,
  BundlingMode,
  CalculatorTab,
  TimeSpan,
  AnalysisType,
  SECONDS_PER_UNIT,
  SECONDS_PER_HOUR,
  IMAGE_SKILL_CONFIGS,
  ImageSkill,
} from "./config"
import { ComputerPanel } from "./computer-panel"
import { ImagePanel } from "./image-panel"
import { VideoPanel } from "./video-panel"

export const title = "Slider with Step Indicators"

const WORKFLOW_BATCH_SIZE = 1_000
const DEFAULT_BUNDLING_MODE: BundlingMode = "bundled"
const INPUT_TOKENS_PER_REQUEST = 800
const COMPUTER_OUTPUT_TOKENS_PER_REQUEST = 50
const OUTPUT_TOKENS_PER_ANALYSIS: Record<AnalysisType, number> = {
  caption: 100,
  point: 12,
  query: 50,
  "object detection": 8,
}
const TOKEN_COST_PER_MILLION = {
  input: 0.3,
  output: 2.5,
}
const MILLION = 1_000_000
const IMAGE_REQUESTS_PER_UNIT = 1_000
const IMAGE_UNIT_LABEL = "1,000 images"

const INITIAL_IMAGE_COMPLEXITY_INDEXES: Record<ImageSkill, number> = Object.fromEntries(
  (Object.keys(IMAGE_SKILL_CONFIGS) as ImageSkill[]).map((skill) => [skill, 0])
) as Record<ImageSkill, number>

type CostEstimate = {
  requestsPerUnit: number
  inputTokensPerUnit: number
  outputTokensPerUnit: number
  totalCostPerUnit: number
  unitLabel: string
}

type EstimatedCostCardProps = {
  estimate: CostEstimate
}

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
})

function formatCompact(value: number) {
  if (!Number.isFinite(value)) {
    return "-"
  }
  if (value === 0) {
    return "0"
  }
  if (Math.abs(value) < 1_000) {
    const rounded = Math.round(value * 10) / 10
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
  }
  const rounded = Math.round(value)
  return compactNumberFormatter.format(rounded)
}

const DIGIT_VALUES = Array.from({ length: 10 }, (_, value) => value)
const DIGIT_STEP_PERCENT = 100 / DIGIT_VALUES.length

type RollingDigitProps = {
  digit: number
  duration: number
  delay?: number
}

function RollingDigit({ digit, duration, delay = 0 }: RollingDigitProps) {
  const safeDigit = Number.isFinite(digit) ? Math.min(Math.max(Math.round(digit), 0), 9) : 0

  return (
    <div className="relative h-[1.3em] w-[0.9ch] overflow-hidden text-center leading-none">
      <motion.div
        initial={false}
        animate={{ y: `-${safeDigit * DIGIT_STEP_PERCENT}%` }}
        transition={{ duration, ease: "easeInOut", delay }}
        className="flex flex-col will-change-transform"
      >
        {DIGIT_VALUES.map((value) => (
          <span key={value} className="block h-[1.3em] leading-none">
            {value}
          </span>
        ))}
      </motion.div>
    </div>
  )
}

function RollingAmount({ amount, previousAmount }: { amount: number; previousAmount: number }) {
  const safeAmount = Number.isFinite(amount) ? amount : 0
  const fixed = safeAmount.toFixed(2)
  const [integerPartRaw, decimalPartRaw = "00"] = fixed.split(".")
  const formattedInteger = Number(integerPartRaw).toLocaleString("en-US")
  const integerChars = formattedInteger.split("")
  const decimalChars = decimalPartRaw.padEnd(2, "0").slice(0, 2).split("")
  const previous = Number.isFinite(previousAmount) ? previousAmount : safeAmount
  const isIncreasing = safeAmount >= previous
  const dollarDuration = isIncreasing ? 0.25 : 0.3
  const tensOfCentsDuration = isIncreasing ? 0.35 : 0.4
  const penniesDuration = isIncreasing ? 0.45 : 0.5
  const tensDelay = 0.08
  const penniesDelay = 0.16

  return (
    <div className="flex flex-wrap items-baseline gap-2 text-4xl font-semibold text-foreground tabular-nums tracking-tight">
      <span className="text-muted-foreground text-2xl">$</span>
      <div className="flex items-baseline gap-0.5">
        {integerChars.map((char, index) => {
          const isDigit = /\d/.test(char)
          if (!isDigit) {
            return (
              <span key={`sep-${index}`} className="text-3xl leading-none">
                {char}
              </span>
            )
          }

          return (
            <RollingDigit
              key={`int-${index}`}
              digit={Number(char)}
              duration={dollarDuration}
            />
          )
        })}
        <span className="text-3xl leading-none">.</span>
        {decimalChars.map((char, index) => (
          <RollingDigit
            key={`dec-${index}`}
            digit={Number(char)}
            duration={index === 0 ? tensOfCentsDuration : penniesDuration}
            delay={index === 0 ? tensDelay : penniesDelay}
          />
        ))}
      </div>
    </div>
  )
}

function usePrevious<T>(value: T) {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

function EstimatedCostCard({ estimate }: EstimatedCostCardProps) {
  const { totalCostPerUnit, requestsPerUnit, inputTokensPerUnit, outputTokensPerUnit, unitLabel } = estimate
  const previousAmount = usePrevious(totalCostPerUnit)
  const formattedRequests = formatCompact(requestsPerUnit)
  const formattedInputTokens = formatCompact(inputTokensPerUnit)
  const formattedOutputTokens = formatCompact(outputTokensPerUnit)

  return (
    <Card className="w-full lg:max-w-sm">
      <CardHeader>
        <CardTitle>Estimated cost</CardTitle>
        <CardDescription>Based on the sliders you choose</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <RollingAmount amount={totalCostPerUnit} previousAmount={previousAmount} />
            <span className="text-base font-medium text-muted-foreground">/ {unitLabel}</span>
          </div>
          <p className="text-sm text-muted-foreground">≈ {formattedRequests} inference requests / {unitLabel}</p>
        </div>
        <div className="rounded-md border p-3 text-sm text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Input tokens / {unitLabel}</span>
            <span>{formattedInputTokens}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span>Output tokens / {unitLabel}</span>
            <span>{formattedOutputTokens}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Estimated pricing for planning purposes only. Review Moondream Cloud pricing at {" "}
          <a
            href="https://moondream.ai/pricing"
            className="underline underline-offset-4 hover:text-foreground"
            target="_blank"
            rel="noreferrer"
          >
            moondream.ai/pricing
          </a>
          .
        </p>
      </CardContent>
    </Card>
  )
}






function calculateVideoEstimate(
  timeSpan: TimeSpan,
  frameSelections: Record<TimeSpan, number>,
  analysisType: AnalysisType
): CostEstimate {
  const frameValues = FRAME_RATE_OPTIONS[timeSpan]
  const frameIndex = frameSelections[timeSpan] ?? 0
  const framesPerWindow = frameValues[frameIndex] ?? frameValues[0]
  const framesPerSecond = framesPerWindow / SECONDS_PER_UNIT[timeSpan]
  const requestsPerHour = framesPerSecond * SECONDS_PER_HOUR
  const inputTokensPerHour = requestsPerHour * INPUT_TOKENS_PER_REQUEST
  const outputTokensPerRequest =
    OUTPUT_TOKENS_PER_ANALYSIS[analysisType] ?? OUTPUT_TOKENS_PER_ANALYSIS.query
  const outputTokensPerHour = requestsPerHour * outputTokensPerRequest
  const inputCost = (inputTokensPerHour / MILLION) * TOKEN_COST_PER_MILLION.input
  const outputCost = (outputTokensPerHour / MILLION) * TOKEN_COST_PER_MILLION.output

  return {
    requestsPerUnit: requestsPerHour,
    inputTokensPerUnit: inputTokensPerHour,
    outputTokensPerUnit: outputTokensPerHour,
    totalCostPerUnit: inputCost + outputCost,
    unitLabel: "hour",
  }
}

function calculateComputerEstimate({
  baseInspections,
  retryPercent,
}: {
  baseInspections: number
  retryPercent: number
}): CostEstimate {
  const totalInspections = Math.max(baseInspections, 0)
  const bundling = BUNDLING_PRESETS[DEFAULT_BUNDLING_MODE]
  const asksPerCall = Math.max(bundling.avgAsksPerCall, 1)
  const retryMultiplier = 1 + Math.max(retryPercent, 0) / 100
  const requestsPerWorkflow = (totalInspections / asksPerCall) * retryMultiplier
  const inputTokensPerWorkflow = requestsPerWorkflow * INPUT_TOKENS_PER_REQUEST
  const outputTokensPerWorkflow = requestsPerWorkflow * COMPUTER_OUTPUT_TOKENS_PER_REQUEST
  const inputCost = (inputTokensPerWorkflow / MILLION) * TOKEN_COST_PER_MILLION.input
  const outputCost = (outputTokensPerWorkflow / MILLION) * TOKEN_COST_PER_MILLION.output
  const scale = WORKFLOW_BATCH_SIZE

  return {
    requestsPerUnit: requestsPerWorkflow * scale,
    inputTokensPerUnit: inputTokensPerWorkflow * scale,
    outputTokensPerUnit: outputTokensPerWorkflow * scale,
    totalCostPerUnit: (inputCost + outputCost) * scale,
    unitLabel: "1,000 workflows",
  }
}

function calculateImageEstimate({
  skill,
  complexityIndex,
}: {
  skill: ImageSkill
  complexityIndex: number
}): CostEstimate {
  const skillConfig = IMAGE_SKILL_CONFIGS[skill] ?? IMAGE_SKILL_CONFIGS.query
  const stops = skillConfig.stops
  const safeIndex = Math.min(Math.max(complexityIndex, 0), Math.max(stops.length - 1, 0))
  const stop = stops[safeIndex] ?? stops[0]
  const outputTokensPerRequest = stop?.tokens ?? 0
  const requestsPerUnit = IMAGE_REQUESTS_PER_UNIT
  const inputTokensPerUnit = requestsPerUnit * INPUT_TOKENS_PER_REQUEST
  const outputTokensPerUnit = requestsPerUnit * outputTokensPerRequest
  const inputCost = (inputTokensPerUnit / MILLION) * TOKEN_COST_PER_MILLION.input
  const outputCost = (outputTokensPerUnit / MILLION) * TOKEN_COST_PER_MILLION.output

  return {
    requestsPerUnit,
    inputTokensPerUnit,
    outputTokensPerUnit,
    totalCostPerUnit: inputCost + outputCost,
    unitLabel: IMAGE_UNIT_LABEL,
  }
}

export function Calculator() {
  const [activeTab, setActiveTab] = useState<CalculatorTab>("video")
  const [timeSpan, setTimeSpan] = useState<TimeSpan>("minute")
  const [frameSelections, setFrameSelections] = useState<Record<TimeSpan, number>>({
    minute: 0,
    second: 0,
  })
  const [analysisType, setAnalysisType] = useState<AnalysisType>("query")
  const [inspectionIndex, setInspectionIndex] = useState(DEFAULT_AI_INSPECTION_INDEX)
  const [retryPercent, setRetryPercent] = useState(0)
  const [imageSkill, setImageSkill] = useState<ImageSkill>("query")
  const [imageComplexityIndexes, setImageComplexityIndexes] = useState<Record<ImageSkill, number>>(
    () => ({ ...INITIAL_IMAGE_COMPLEXITY_INDEXES })
  )

  const imageComplexityIndex = imageComplexityIndexes[imageSkill] ?? 0

  const baseInspections =
    AI_INSPECTION_STOPS[inspectionIndex] ?? AI_INSPECTION_STOPS[DEFAULT_AI_INSPECTION_INDEX]

  const videoEstimate = useMemo(
    () => calculateVideoEstimate(timeSpan, frameSelections, analysisType),
    [timeSpan, frameSelections, analysisType]
  )

  const computerEstimate = useMemo(
    () =>
      calculateComputerEstimate({
        baseInspections,
        retryPercent,
      }),
    [baseInspections, retryPercent]
  )

  const imageEstimate = useMemo(
    () =>
      calculateImageEstimate({
        skill: imageSkill,
        complexityIndex: imageComplexityIndex,
      }),
    [imageSkill, imageComplexityIndex]
  )

  const estimate =
    activeTab === "video"
      ? videoEstimate
      : activeTab === "computer"
        ? computerEstimate
        : imageEstimate

  return (
    <PageContent>
      <SectionHeader
        sectionIcon={<PiggyBank className="size-4" />}
        section="Pricing"
        heading="Estimate your costs"
        description="Adjust the toggles for each workload to see how Moondream Cloud pricing scales for your use case."
      />
      <div className="mt-8 w-full">
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex w-full max-w-xl flex-col gap-6">
            <Tabs value={activeTab} onValueChange={(next) => setActiveTab(next as CalculatorTab)}>
              <TabsList>
                <TabsTrigger value="video">
                  <Video />
                  Process Video
                </TabsTrigger>
                <TabsTrigger value="computer">
                  <Laptop />
                  Computer Use
                </TabsTrigger>
                <TabsTrigger value="image">
                  <ImageIcon />
                  Image Processing
                </TabsTrigger>
              </TabsList>
              <VideoPanel
                timeSpan={timeSpan}
                onTimeSpanChange={setTimeSpan}
                frameSelections={frameSelections}
                onFrameSelectionChange={(span, index) =>
                  setFrameSelections((prev) => ({ ...prev, [span]: index }))
                }
                analysisType={analysisType}
                onAnalysisTypeChange={setAnalysisType}
              />
              <ComputerPanel
                inspectionIndex={inspectionIndex}
                onInspectionIndexChange={setInspectionIndex}
                retryPercent={retryPercent}
                onRetryPercentChange={setRetryPercent}
              />
              <ImagePanel
                skill={imageSkill}
                onSkillChange={setImageSkill}
                complexityIndex={imageComplexityIndex}
                onComplexityIndexChange={(index) =>
                  setImageComplexityIndexes((prev) => ({
                    ...prev,
                    [imageSkill]: index,
                  }))
                }
              />
            </Tabs>
          </div>
          <EstimatedCostCard estimate={estimate} />
        </div>
      </div>
    </PageContent>
  )
}
